import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Typography,
  Alert,
  Card,
  Progress,
} from 'antd';
import zxcvbn from 'zxcvbn';
import { acceptInvite } from '../../services/adminKycApiService';

const { Title, Text } = Typography;

const STRENGTH_COLORS = ['#ff4d4f', '#ff4d4f', '#faad14', '#52c41a', '#52c41a'];
const STRENGTH_LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];

/** Public page for accepting an admin invitation and creating an account. */
export function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [form] = Form.useForm<{ fullName: string; password: string; confirmPassword: string }>();
  const [loading, setLoading] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [password, setPassword] = useState('');

  const strength = password ? zxcvbn(password) : null;
  const score = strength?.score ?? 0;

  const handleSubmit = async (values: { fullName: string; password: string; confirmPassword: string }) => {
    if (!token) {
      setTokenError(true);
      return;
    }
    if (score < 2) {
      form.setFields([{ name: 'password', errors: ['Password is too weak. Please choose a stronger password.'] }]);
      return;
    }
    setLoading(true);
    try {
      await acceptInvite(token, values.fullName, values.password);
      navigate('/login', { state: { message: 'Account created! Please log in.' } });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 400 || status === 404 || status === 410) {
        setTokenError(true);
      } else {
        form.setFields([{ name: 'fullName', errors: ['Something went wrong. Please try again.'] }]);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token || tokenError) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
      }}>
        <Card style={{ maxWidth: 440, width: '100%', borderRadius: 12, padding: 8 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <img src="/tap2serve.png" alt="Tap2Serve" style={{ width: 48, height: 48, marginBottom: 12 }} />
          </div>
          <Alert
            type="error"
            message="Invitation invalid or expired"
            description="This invitation has expired or is invalid. Contact your administrator for a new invitation."
            showIcon
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5',
    }}>
      <Card style={{ maxWidth: 440, width: '100%', borderRadius: 12, padding: 8 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/tap2serve.png" alt="Tap2Serve" style={{ width: 48, height: 48, marginBottom: 12 }} />
          <Title level={3} style={{ marginBottom: 4 }}>Create your admin account</Title>
          <Text type="secondary">Set up your Tap2Serve admin access</Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            name="fullName"
            label="Full name"
            rules={[{ required: true, message: 'Full name is required' }]}
          >
            <Input size="large" placeholder="Jane Smith" autoComplete="name" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Password is required' }]}
          >
            <Input.Password
              size="large"
              placeholder="Choose a strong password"
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>

          {/* Password strength indicator */}
          {password && (
            <div style={{ marginTop: -16, marginBottom: 16 }}>
              <Progress
                percent={(score / 4) * 100}
                showInfo={false}
                strokeColor={STRENGTH_COLORS[score]}
                size="small"
              />
              <Text style={{ fontSize: 12, color: STRENGTH_COLORS[score] }}>
                {STRENGTH_LABELS[score]}
              </Text>
            </div>
          )}

          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
            >
              Create account
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default AcceptInvitePage;
